import {
  UtensilsCrossed, Plus, Trash2, Euro, Package, Tag, Percent,
  ChefHat, Box, X, AlertCircle, Save
} from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { ToggleSwitch as Switch } from "../ui/toggle-switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import IngredienteSelect from "../IngredienteSelect";
import SearchableSelect from "../SearchableSelect";

const formatEuro = (val) => {
  if (val === null || val === undefined) return "—";
  return `€ ${Number(val).toFixed(2)}`;
};

const formatPercentuale = (val) => {
  if (val === null || val === undefined) return "—";
  return `${Number(val).toFixed(1)}%`;
};

const MargineBadge = ({ margine }) => {
  if (margine === null || margine === undefined) return null;
  let color = "bg-red-100 text-red-700";
  if (margine >= 70) color = "bg-green-100 text-green-700";
  else if (margine >= 50) color = "bg-emerald-100 text-emerald-700";
  else if (margine >= 30) color = "bg-yellow-100 text-yellow-700";
  else if (margine >= 15) color = "bg-orange-100 text-orange-700";
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${color}`}>
      <Percent className="w-3 h-3" />
      {formatPercentuale(margine)}
    </span>
  );
};

export const MenuProdottoDialog = ({
  open, onOpenChange, editItem, form, setForm, onSave, saving,
  categorie, ingredienti, categorieIngredienti, ricette, materiali,
  addComponentOpen, setAddComponentOpen, tipoComponente, setTipoComponente,
  nuovoComponente, setNuovoComponente, onAddComponente,
  removeComponente, updateComponenteQuantita, getUnitaDisplay,
  calcolaFoodCost, calcolaFoodCostCibo, calcolaMargine
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-2xl max-h-[100dvh] sm:max-h-[90vh] w-full h-[100dvh] sm:h-auto rounded-none sm:rounded-lg overflow-y-auto p-4 sm:p-6">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <UtensilsCrossed className="w-5 h-5 text-[#FF6584]" />
          {editItem ? "Modifica Prodotto" : "Nuovo Prodotto"}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-6 py-4">
        <div className="grid gap-4">
          <div>
            <Label htmlFor="nome">Nome Prodotto *</Label>
            <Input
              id="nome"
              value={form.nome}
              onChange={(e) => setForm(prev => ({ ...prev, nome: e.target.value }))}
              placeholder="es. Margherita, Carbonara..."
              data-testid="input-nome"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="categoria">Categoria</Label>
              <Select value={form.categoria_id} onValueChange={(v) => setForm(prev => ({ ...prev, categoria_id: v }))}>
                <SelectTrigger data-testid="select-categoria"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                <SelectContent>
                  {categorie.map(c => (<SelectItem key={c.id} value={c.id}>{c.icona} {c.nome}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <div className="flex items-center gap-3">
                <Switch id="attivo" checked={form.attivo} onCheckedChange={(v) => setForm(prev => ({ ...prev, attivo: v }))} data-testid="switch-attivo" />
                <Label htmlFor="attivo" className="font-normal">Attivo</Label>
              </div>
            </div>
          </div>

          <div>
            <Label htmlFor="descrizione">Descrizione (opzionale)</Label>
            <Textarea
              id="descrizione"
              value={form.descrizione}
              onChange={(e) => setForm(prev => ({ ...prev, descrizione: e.target.value }))}
              placeholder="Descrizione breve del prodotto..."
              rows={2}
              data-testid="input-descrizione"
            />
          </div>
        </div>

        {/* Componenti */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <Label className="text-base font-semibold">Componenti</Label>
            <Button size="sm" variant="outline" onClick={() => setAddComponentOpen(true)} className="gap-1" data-testid="aggiungi-componente-btn">
              <Plus className="w-4 h-4" />Aggiungi
            </Button>
          </div>

          {form.componenti.length === 0 ? (
            <div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
              <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">Nessun componente aggiunto</p>
              <p className="text-xs text-gray-400">Aggiungi ingredienti, ricette o packaging</p>
            </div>
          ) : (
            <div className="space-y-2">
              {form.componenti.map((comp, idx) => {
                const unitaDisplay = getUnitaDisplay(comp);
                return (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-testid={`componente-${idx}`}>
                    <div className="p-1.5 rounded-lg bg-white">
                      {comp.tipo === "ingrediente" && <Package className="w-4 h-4 text-green-600" />}
                      {comp.tipo === "ricetta" && <ChefHat className="w-4 h-4 text-purple-600" />}
                      {comp.tipo === "packaging" && <Box className="w-4 h-4 text-orange-600" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1">
                        <p className="font-medium text-gray-900 text-sm truncate">{comp.nome}</p>
                        {comp.tipo === "ricetta" && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-600 shrink-0">RICETTA</span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <Input type="number" step="1" min="0" value={comp.quantita || ""} onChange={(e) => updateComponenteQuantita(idx, e.target.value)} className="h-6 w-20 text-xs px-2 py-0" />
                        <span className="text-xs text-gray-500">{unitaDisplay}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900 text-sm" data-testid={`componente-costo-totale-${idx}`}>{formatEuro(comp.costo_totale)}</p>
                      <p className="text-xs text-gray-500">{formatEuro(comp.costo_unitario)}/{(comp.tipo === "packaging" || comp.unita_misura === "pz") ? "pz" : "kg"} <span className="text-gray-400">IVA incl.</span></p>
                      {comp.iva_percentuale != null && comp.prezzo_netto != null ? (
                        <p className="text-[10px] text-gray-400" data-testid={`componente-iva-${idx}`}>
                          netto {formatEuro(comp.prezzo_netto)}/{(comp.tipo === "packaging" || comp.unita_misura === "pz") ? "pz" : "kg"} + IVA {comp.iva_percentuale}%
                        </p>
                      ) : (
                        comp.tipo === "ricetta" && (
                          <p className="text-[10px] text-gray-400" data-testid={`componente-iva-${idx}`}>IVA già inclusa</p>
                        )
                      )}
                    </div>
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-gray-400 hover:text-red-500" onClick={() => removeComponente(idx)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Riepilogo costi */}
        <div className="bg-gradient-to-r from-[#FF6584]/10 to-[#6C63FF]/10 rounded-xl p-5 border border-[#FF6584]/20">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Euro className="w-4 h-4 text-[#FF6584]" />Calcolo Prezzo
          </h4>
          <div className="flex items-center justify-between py-2 border-b border-gray-200">
            <span className="text-gray-600 text-sm">Food Cost <span className="text-gray-400">(solo cibo)</span></span>
            <span className="text-base font-semibold text-emerald-700">{formatEuro(calcolaFoodCostCibo ? calcolaFoodCostCibo() : calcolaFoodCost())}</span>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-gray-200">
            <span className="text-gray-700 font-medium">Totale <span className="text-gray-400 font-normal">(cibo + packaging, IVA incl.)</span></span>
            <span className="text-xl font-bold text-gray-900">{formatEuro(calcolaFoodCost())}</span>
          </div>
          <div className="py-4 border-b border-gray-200">
            <Label htmlFor="prezzo" className="text-gray-600 mb-2 block">Prezzo di Vendita (€)</Label>
            <Input
              id="prezzo" type="number" step="0.01" min="0"
              value={form.prezzo_vendita}
              onChange={(e) => setForm(prev => ({ ...prev, prezzo_vendita: e.target.value }))}
              placeholder="Inserisci il prezzo..."
              className="text-xl font-bold h-12 text-[#FF6584]"
              data-testid="input-prezzo"
            />
          </div>
          <div className="flex items-center justify-between pt-3">
            <span className="text-gray-600">Margine</span>
            <MargineBadge margine={calcolaMargine()} />
          </div>
          {calcolaMargine() < 30 && parseFloat(form.prezzo_vendita) > 0 && (
            <div className="mt-4 p-3 bg-orange-50 rounded-lg flex items-center gap-2 text-orange-700 text-sm">
              <AlertCircle className="w-4 h-4 shrink-0" /><span>Margine basso! Considera di aumentare il prezzo.</span>
            </div>
          )}
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onSave} disabled={saving} className="bg-[#FF6584] hover:bg-[#E85570]" data-testid="salva-prodotto-btn">
          {saving ? "Salvataggio..." : (<><Save className="w-4 h-4 mr-2" />Salva</>)}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const MenuComponenteDialog = ({
  open, onOpenChange, tipoComponente, setTipoComponente,
  nuovoComponente, setNuovoComponente, onAddComponente,
  ingredienti, categorieIngredienti, ricette, materiali
}) => (
  <Dialog open={open} onOpenChange={(o) => {
    onOpenChange(o);
    if (!o) {
      setTipoComponente("ingrediente");
      setNuovoComponente({ ingrediente_id: "", ricetta_id: "", packaging_id: "", quantita: "" });
    }
  }}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          {tipoComponente === "ricetta" ? (<><ChefHat className="w-5 h-5 text-purple-600" /> Aggiungi Ricetta</>) :
           tipoComponente === "packaging" ? (<><Box className="w-5 h-5 text-orange-600" /> Aggiungi Packaging</>) :
           (<><Package className="w-5 h-5 text-green-600" /> Aggiungi Ingrediente</>)}
        </DialogTitle>
      </DialogHeader>

      <div className="space-y-4 py-4">
        <Tabs value={tipoComponente} onValueChange={(v) => {
          setTipoComponente(v);
          setNuovoComponente({ ingrediente_id: "", ricetta_id: "", packaging_id: "", quantita: "" });
        }}>
          <TabsList className="grid grid-cols-3 w-full">
            <TabsTrigger value="ingrediente" className="text-xs gap-1"><Package className="w-3 h-3" />Ingrediente</TabsTrigger>
            <TabsTrigger value="ricetta" className="text-xs gap-1"><ChefHat className="w-3 h-3" />Ricetta</TabsTrigger>
            <TabsTrigger value="packaging" className="text-xs gap-1"><Box className="w-3 h-3" />Packaging</TabsTrigger>
          </TabsList>

          <TabsContent value="ingrediente" className="mt-4 space-y-4">
            <div>
              <Label>Ingrediente *</Label>
              <IngredienteSelect ingredienti={ingredienti} categorie={categorieIngredienti} value={nuovoComponente.ingrediente_id} onChange={(id) => setNuovoComponente(prev => ({ ...prev, ingrediente_id: id }))} placeholder="Cerca e seleziona ingrediente..." />
            </div>
            <div>
              <Label>Quantità ({ingredienti.find(i => i.id === nuovoComponente.ingrediente_id)?.unita_misura === "pz" ? "pezzi" : "grammi"}) *</Label>
              <Input type="number" step="1" min="0" value={nuovoComponente.quantita} onChange={(e) => setNuovoComponente(prev => ({ ...prev, quantita: e.target.value }))} placeholder="es. 200" />
            </div>
            {nuovoComponente.ingrediente_id && nuovoComponente.quantita && (
              <div className="p-3 bg-green-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Costo stimato:</span>
                  <span className="font-bold text-green-600">
                    {(() => {
                      const ing = ingredienti.find(i => i.id === nuovoComponente.ingrediente_id);
                      const quantita = parseFloat(nuovoComponente.quantita) || 0;
                      const iva = ing?.iva_percentuale != null ? Number(ing.iva_percentuale) : 10;
                      const prezzoKg = (ing?.prezzo_attuale || 0) * (1 + iva / 100);
                      const unitaMisura = ing?.unita_misura || "kg";
                      const costo = unitaMisura === "pz" ? quantita * prezzoKg : (quantita / 1000) * prezzoKg;
                      return formatEuro(costo);
                    })()}
                  </span>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="ricetta" className="mt-4 space-y-4">
            <div>
              <Label>Ricetta (Semilavorato) *</Label>
              <SearchableSelect items={ricette} value={nuovoComponente.ricetta_id} onChange={(id) => setNuovoComponente(prev => ({ ...prev, ricetta_id: id }))} placeholder="Cerca e seleziona ricetta..." searchPlaceholder="Cerca ricetta..." icon={ChefHat} getItemLabel={(r) => r.nome} getItemSubtext={(r) => `${formatEuro(r.costo_per_kg)}/kg`} emptyMessage="Nessuna ricetta trovata" />
            </div>
            <div>
              <Label>Quantità (grammi) *</Label>
              <Input type="number" step="1" min="0" value={nuovoComponente.quantita} onChange={(e) => setNuovoComponente(prev => ({ ...prev, quantita: e.target.value }))} placeholder="es. 200" />
            </div>
            {nuovoComponente.ricetta_id && nuovoComponente.quantita && (
              <div className="p-3 bg-purple-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Costo stimato:</span>
                  <span className="font-bold text-purple-600">
                    {formatEuro(((parseFloat(nuovoComponente.quantita) || 0) / 1000) * (ricette.find(r => r.id === nuovoComponente.ricetta_id)?.costo_per_kg || 0))}
                  </span>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="packaging" className="mt-4 space-y-4">
            <div>
              <Label>Packaging *</Label>
              <SearchableSelect items={materiali} value={nuovoComponente.packaging_id} onChange={(id) => setNuovoComponente(prev => ({ ...prev, packaging_id: id }))} placeholder="Cerca e seleziona packaging..." searchPlaceholder="Cerca packaging..." icon={Box} getItemLabel={(m) => m.nome} getItemSubtext={(m) => { const prezzo = m.prezzo_unitario || m.prezzo_ultimo || m.prezzo_migliore || 0; return prezzo > 0 ? `${formatEuro(prezzo)}/pz` : "prezzo n.d."; }} emptyMessage="Nessun packaging trovato" />
            </div>
            <div>
              <Label>Quantità (pezzi) *</Label>
              <Input type="number" step="1" min="0" value={nuovoComponente.quantita} onChange={(e) => setNuovoComponente(prev => ({ ...prev, quantita: e.target.value }))} placeholder="es. 1" />
            </div>
            {nuovoComponente.packaging_id && nuovoComponente.quantita && (
              <div className="p-3 bg-orange-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Costo stimato:</span>
                  <span className="font-bold text-orange-600">
                    {(() => {
                      const mat = materiali.find(m => m.id === nuovoComponente.packaging_id);
                      const iva = mat?.iva_percentuale != null ? Number(mat.iva_percentuale) : 22;
                      const prezzo = (mat?.prezzo_unitario || mat?.prezzo_ultimo || mat?.prezzo_migliore || 0) * (1 + iva / 100);
                      return formatEuro((parseFloat(nuovoComponente.quantita) || 0) * prezzo);
                    })()}
                  </span>
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
        <Button onClick={onAddComponente} className={tipoComponente === "ricetta" ? "bg-purple-600 hover:bg-purple-700" : tipoComponente === "packaging" ? "bg-orange-600 hover:bg-orange-700" : "bg-green-600 hover:bg-green-700"}>
          <Plus className="w-4 h-4 mr-2" />Aggiungi
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const MenuCategoriaDialog = ({
  open, onOpenChange, categorie, nuovaCategoria, setNuovaCategoria,
  onSave, onDeleteCategoria
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md">
      <DialogHeader>
        <DialogTitle>Gestione Categorie Menu</DialogTitle>
      </DialogHeader>

      <div className="space-y-4 py-4">
        {categorie.length > 0 && (
          <div className="space-y-2">
            <Label className="text-sm text-gray-500">Categorie esistenti</Label>
            {categorie.map(cat => (
              <div key={cat.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                <span className="flex items-center gap-2">
                  <span>{cat.icona}</span>
                  <span className="font-medium">{cat.nome}</span>
                </span>
                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-500 hover:text-red-600" onClick={() => onDeleteCategoria(cat.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        )}

        <div className="pt-4 border-t">
          <Label className="text-sm text-gray-500 mb-2 block">Nuova categoria</Label>
          <div className="flex gap-2">
            <Input value={nuovaCategoria.icona} onChange={(e) => setNuovaCategoria(prev => ({ ...prev, icona: e.target.value }))} className="w-16 text-center text-lg" placeholder="🍽️" />
            <Input value={nuovaCategoria.nome} onChange={(e) => setNuovaCategoria(prev => ({ ...prev, nome: e.target.value }))} placeholder="Nome categoria" className="flex-1" />
          </div>
        </div>
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={() => onOpenChange(false)}>Chiudi</Button>
        <Button onClick={onSave} className="bg-[#FF6584] hover:bg-[#E85570]">
          <Plus className="w-4 h-4 mr-2" />Aggiungi
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
